import { DynamoDBEventType } from 'src/enums/dynamodb-event-type';

export enum CohortStepType {
  QueryValidation = 'QUERY_VALIDATION',
  DataExtraction = 'DATA_EXTRACTION',
  CodesSelection = 'CODES_SELECTION',
  Results = 'RESULTS',
}

export enum CohortStepStatus {
  In_Progress = 'IN_PROGRESS',
  Completed = 'COMPLETED',
  Error = 'ERROR',
}

export enum CohortStepSubstatus {
  None = 'NONE',
  Awaiting_User_Decision = 'AWAITING_USER_DECISION',
  Editing = 'EDITING',
}

export interface CohortStepResult {
  status: CohortStepStatus;
  data: StepContent[];
  userQuery?: any;
  updatedAt: string;
}

export interface StepPayload {
  content: string | SearchTerm[] | string[];
  format: string;
}

export interface StepContent {
  type: string;
  payload: StepPayload;
}

export interface DataSetWikiMetadata {
  name: string;
  description: string;
  link?: string;
  provider_name?: string;
  date_range?: DateRange;
  record_count?: number;
  total: number;
  tables: DataSourceWikiTable[];
  demographics: Distribution[];
  highlights: Distribution[];
}

export interface DateRange {
  min_date: string;
  max_date: string;
}

export interface Distribution {
  metric: string;
  distribution: Record<string, number>;
  mean_patient_years?: number;
  cumulative_patient_years?: number;
}

export interface DataSourceWikiTable {
  name: string;
  description: string;
  record_count: number;
  columns: DataSourceWikiTableColumn[];
}

export interface DataSourceWikiTableColumn {
  name: string;
  type: string;
  description: string;
  completeness_rate?: number;
}

export interface DataSetWiki {
  id: string;
  metadata: DataSetWikiMetadata;
}

export interface ConceptCode {
  concept_name: string;
  concept_id: string;
  relevance: string;
  enabled: boolean;
  concept_count: number;
}

export interface CodeSet {
  name: string;
  data: ConceptCode[];
  description: string;
}

export interface SearchTerm {
  column_name: string;
  search_term: string;
  table_name: string;
  matches: {
    saved: {
      code_list_id: string;
    };
    recommended: CodeSet;
  };
}

export interface ConceptCodesPayload {
  type: string;
  payload: {
    type: string;
    content: SearchTerm[];
  };
}

export interface ResultPayload extends StepPayload {
  content: string;
  format: 'markdown' | 'json';
}

export interface CohortStepData {
  data: Array<StepContent>;
  status: CohortStepStatus;
}

export interface BaseStep {
  status: CohortStepStatus;
  type: CohortStepType;
  data: Array<StepContent | ConceptCodesPayload>;
}

export interface QueryValidationStep extends BaseStep {
  type: CohortStepType.QueryValidation;
  data: StepContent[];
}

export interface DataExtractionStep extends BaseStep {
  type: CohortStepType.DataExtraction;
  data: StepContent[];
}

export interface CodesSelectionStep extends BaseStep {
  type: CohortStepType.CodesSelection;
  data: Array<ConceptCodesPayload>;
}

export interface ResultStep extends BaseStep {
  type: CohortStepType.Results;
  data: StepContent[];
}

export type CohortStep = QueryValidationStep | DataExtractionStep | CodesSelectionStep | ResultStep;

export interface DynamoDBData {
  CohortId: string;
  CreatedAt: string;
  Name: string;
  Description: string;
  CurrentStep: CohortStepType;
  DataSourceId: string;
  UpdateAt: string;
  UserId: string;
  UserQuery: string;
  PK: string;
  SK: string;
  Steps: Record<CohortStepType, CohortStepData>;
  _et: string;
  [key: string]: unknown;
}

export interface DynamoDBUpdate {
  id: string;
  timestamp: string;
  eventType: DynamoDBEventType;
  newData?: DynamoDBData;
  oldData?: DynamoDBData;
}

export interface StepConfig {
  title: string;
  statusText?: string;
}

export interface BaseStepComponentProps {
  isActive: boolean;
  isCompleted: boolean;
  onAccept: (stepType: CohortStepType) => void;
  onEdit: ({ step, editedContent }: { step: CohortStepType; editedContent: string }) => void;
  onCreateCohort: () => void;
  isRunJobPending?: boolean;
}

export interface QueryValidationStepProps extends BaseStepComponentProps {
  step: QueryValidationStep;
}

export interface DataExtractionStepProps extends BaseStepComponentProps {
  step: DataExtractionStep;
}

export interface CodesSelectionStepProps extends BaseStepComponentProps {
  step: CodesSelectionStep;
}

export interface ResultStepProps extends BaseStepComponentProps {
  step: ResultStep;
}

export type StepComponentProps =
  | DataExtractionStepProps
  | QueryValidationStepProps
  | CodesSelectionStepProps
  | ResultStepProps;

export type StepComponents = {
  [CohortStepType.QueryValidation]: React.ComponentType<QueryValidationStepProps>;
  [CohortStepType.DataExtraction]: React.ComponentType<DataExtractionStepProps>;
  [CohortStepType.CodesSelection]: React.ComponentType<CodesSelectionStepProps>;
  [CohortStepType.Results]: React.ComponentType<ResultStepProps>;
};

export interface Cohort {
  cohortId: string;
  dataSourceId: string;
  name: string;
  userQuery: string;
  createdBy: string;
  description: string;
  currentStep: CohortStepType;
  steps: {
    [key in CohortStepType]?: CohortStepResult;
  };
  extras: Record<string, any>;
  createdAt?: string;
  updatedAt?: string;
  deletedAt?: string | null;
  dataSourceName?: string;
  createdByEmail?: string;
  dataWiki: DataSetWiki;
  highlightStatus?: string;
  archivedAt?: string | null;
}

export interface CohortsQueryDTO {
  data: {
    cohorts: Cohort[];
    lastEvaluatedKey?: string;
  };
}

export type CohortState = {
  cohortId: string | null;
  currentStep: CohortStepType | null;
  dataSourceId: string | null;
  name: string | null;
  description: string | null;
  steps: Record<CohortStepType, CohortStepData> | Record<string, CohortStepData>;
  userId: string | null;
  userQuery: string;
  rootOrganizationId: string | null;
  updateStatus: 'idle' | 'connecting' | 'connected' | 'error' | 'disconnected';
  error: string | null;
  streamFilter?: { pk?: string | null; sk?: string; eventTypes?: string };
  allCodesets?: any[];
  codesContent: Record<string, any>;
  activeTabs: Record<number, 'saved' | 'recommended'>;
};

export enum CohortActionTypes {
  UpdateCohortDetails = 'createCohort/UpdateCohortDetails',
}

export interface CohortByIdQueryDTO {
  data: {
    cohort: Cohort;
  };
}
